import { isStorageQuotaError } from "@/gm/gm-monster-catalog-idb";
import type { DungeonMonsterAllowance } from "@/dungeon/dungeon-shop-types";
import type { TowerFloorConfig, TowerFloorSpawnRow } from "@/tower/tower-floor-config";
import {
  clampSpawnPerMinute,
  legacySpawnFrequencyToPerMinute,
} from "@/tower/tower-spawn-rate";

export type DungeonFloorConfig = TowerFloorConfig & {
  monsterAllowance?: DungeonMonsterAllowance;
};

const DB_NAME = "mind-survival-dungeon";
const STORE = "kv";
const DB_VERSION = 1;
const CONFIG_KEY_PREFIX = "dungeon-floor-config/v1:";

let dbPromise: Promise<IDBDatabase> | null = null;
const configsCache = new Map<number, DungeonFloorConfig>();

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("เปิด IndexedDB ไม่สำเร็จ"));
  });
  return dbPromise;
}

function configKey(userId: string, floor: number): string {
  return `${CONFIG_KEY_PREFIX}${userId}:${floor}`;
}

export async function hydrateDungeonFloorConfigs(userId: string): Promise<void> {
  configsCache.clear();
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve();
        return;
      }
      const key = String(cursor.key);
      if (key.startsWith(`${CONFIG_KEY_PREFIX}${userId}:`)) {
        const floor = Number(key.split(":").pop());
        const val = cursor.value as DungeonFloorConfig;
        if (Number.isFinite(floor) && val) configsCache.set(floor, val);
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error ?? new Error("อ่าน dungeon config ไม่สำเร็จ"));
  });
}

export function getDungeonFloorConfig(floor: number): DungeonFloorConfig | null {
  return configsCache.get(floor) ?? null;
}

export function listDungeonFloorConfigs(): DungeonFloorConfig[] {
  return [...configsCache.values()];
}

export async function saveDungeonFloorConfig(
  userId: string,
  config: DungeonFloorConfig,
): Promise<void> {
  configsCache.set(config.floor, config);
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("บันทึก dungeon config ไม่สำเร็จ"));
      tx.objectStore(STORE).put(config, configKey(userId, config.floor));
    });
  } catch (err) {
    if (isStorageQuotaError(err)) {
      throw new Error("พื้นที่เก็บข้อมูลเต็ม");
    }
    throw err;
  }
}

export function isActiveDungeonFloorConfig(config: DungeonFloorConfig | null | undefined): boolean {
  if (!config || !config.enabled) return false;
  return config.spawns.length > 0;
}

export function normalizeDungeonSpawnRow(raw: unknown): TowerFloorSpawnRow | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<TowerFloorSpawnRow> & { spawnFrequency?: number };
  if (typeof r.monsterId !== "string" || !r.monsterId) return null;
  const row: TowerFloorSpawnRow = {
    monsterId: r.monsterId,
    count: Math.max(1, Math.floor(typeof r.count === "number" ? r.count : 1)),
  };
  if (r.powerMultiplier != null) row.powerMultiplier = r.powerMultiplier;
  if (r.spawnPerMinute != null) {
    row.spawnPerMinute = clampSpawnPerMinute(r.spawnPerMinute);
  } else if (r.spawnFrequency != null) {
    row.spawnPerMinute = legacySpawnFrequencyToPerMinute(r.spawnFrequency);
  }
  return row;
}
